import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PromiseStatuses } from "../../../../utils";
import { GetAllManipulationDetailFiltersDTO, GetAllManipulationDetailsDTO, ManipulationDetailDTO } from "./dataservice/dto";
import { NewManipulationDetailService } from "./dataservice/service";

interface ManipulationDetailState {
    // REQUEST
    manipulationDetailCreationRequest: ManipulationDetailDTO
    
    // FILTERS
    manipulationDetailFilters: GetAllManipulationDetailFiltersDTO

    // STATUS
    manipulationDetailCreationStatus: PromiseStatuses
    manipulationDetailDeleteStatus: PromiseStatuses
    getAllManipulationDetailStatus: PromiseStatuses
    getManipulationDetailByIdStatus: PromiseStatuses

    // RESPONSE
    getAllManipulationDetailResponse?: GetAllManipulationDetailsDTO
    getManipulationDetailByIdResponse?: ManipulationDetailDTO
}

const initialState: ManipulationDetailState = {
    manipulationDetailCreationRequest: {},

    manipulationDetailFilters: {
        itemsPerPage: 0,
        page: 0
    },

    manipulationDetailCreationStatus: 'idle',
    manipulationDetailDeleteStatus: 'idle',
    getAllManipulationDetailStatus: 'idle',
    getManipulationDetailByIdStatus: 'idle'
}

export const ManipulationDetailCreation = createAsyncThunk(
    'ManipulationDetail/Creation',
    async (data: ManipulationDetailDTO, thunkApi): Promise<void> => {
        const ManipulationDetailService = NewManipulationDetailService()
        return ManipulationDetailService.CreateManipulationDetail(data).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetAllManipulationDetail = createAsyncThunk(
    'ManipulationDetail/GetAll',
    async (filters: GetAllManipulationDetailFiltersDTO, thunkApi): Promise<GetAllManipulationDetailsDTO> => {
        const ManipulationDetailService = NewManipulationDetailService()
        return ManipulationDetailService.GetAllManipulationDetails(filters).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetManipulationDetailById = createAsyncThunk(
    'ManipulationDetail/GetById',
    async (id: string, thunkApi): Promise<ManipulationDetailDTO> => {
        const ManipulationDetailService = NewManipulationDetailService()
        return ManipulationDetailService.GetManipulationDetailById(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const ManipulationDetailDelete = createAsyncThunk(
    'ManipulationDetail/Delete',
    async (id: string, thunkApi): Promise<void> => {
        const ManipulationDetailService = NewManipulationDetailService()
        return ManipulationDetailService.DeleteManipulationDetail(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

const ManipulationDetailSlice = createSlice({
    name: 'manipulationDetail/slice',
    initialState,
    reducers: {

        // REQUEST
        setMDCreationGGNotificationId: (state, action) => {
            state.manipulationDetailCreationRequest.ggNotificationId = action.payload
        },
        setMDCreationCompanyName: (state, action) => {
            state.manipulationDetailCreationRequest.companyName = action.payload
        },
        setMDCreationHeadquarter: (state, action) => {
            state.manipulationDetailCreationRequest.headQuarter = action.payload
        },
        setMDCreationContact: (state, action) => {
            state.manipulationDetailCreationRequest.contact = action.payload
        },
        setMDCreationGGGNGLN: (state, action) => {
            state.manipulationDetailCreationRequest.ggnGln = action.payload
        },
        setMDCreationVatOrFiscalCode: (state, action) => {
            state.manipulationDetailCreationRequest.vatOrFiscalCode = action.payload
        },
        setMDCreationCoordinates: (state, action) => {
            state.manipulationDetailCreationRequest.coordinates = action.payload
        },
        setMDCreationThirdPartyPackaging: (state, action) => {
            state.manipulationDetailCreationRequest.thirdPartyPackaging = action.payload
        },
        setMDCreationIsCertified: (state, action) => {
            state.manipulationDetailCreationRequest.isCertified = action.payload
        },
        setMDCreationParallelProperty: (state, action) => {
            state.manipulationDetailCreationRequest.parallelProperty = action.payload
        },
        setMDCreationProducts: (state, action) => {
            state.manipulationDetailCreationRequest.products = action.payload
        },
        resetManipulationDetailRequest: (state) => {
            state.manipulationDetailCreationRequest = {}
        },

        // FILTERS
        setMDFiltersItemsPerPage: (state, action) => {
            state.manipulationDetailFilters.itemsPerPage = action.payload
        },
        setMDFiltersPage: (state, action) => {
            state.manipulationDetailFilters.page = action.payload
        },
        setMDFiltersGGNotificationId: (state, action) => {
            state.manipulationDetailFilters.ggNotificationId = action.payload
        },
        resetManipulationDetailFilters: (state) => {
            state.manipulationDetailFilters = {
                itemsPerPage: 0,
                page: 0
            }
        },

        // STATUS
        resetManipulationDetailCreationStatus: (state) => {
            state.manipulationDetailCreationStatus = 'idle'
        },
        resetGetAllManipulationDetailStatus: (state) => {
            state.getAllManipulationDetailStatus = 'idle'
        },
        resetManipulationDetailDeleteStatus: (state) => {
            state.manipulationDetailDeleteStatus = 'idle'
        },
        resetGetManipulationDetailByIdStatus: (state) => {
            state.getManipulationDetailByIdStatus = 'idle'
        }
    },
    extraReducers(builder) {
        builder

            // CREATION
            .addCase(ManipulationDetailCreation.pending, (state) => {
                state.manipulationDetailCreationStatus = 'loading'
            })
            .addCase(ManipulationDetailCreation.fulfilled, (state) => {
                state.manipulationDetailCreationStatus = 'successfully'
            })
            .addCase(ManipulationDetailCreation.rejected, (state, action) => {
                state.manipulationDetailCreationStatus = 'failed'
            })

            // GET ALL
            .addCase(GetAllManipulationDetail.pending, (state) => {
                state.getAllManipulationDetailStatus = 'loading'
            })
            .addCase(GetAllManipulationDetail.fulfilled, (state, action) => {
                state.getAllManipulationDetailStatus = 'successfully'
                state.getAllManipulationDetailResponse = action.payload
            })
            .addCase(GetAllManipulationDetail.rejected, (state, action) => {
                state.getAllManipulationDetailStatus = 'failed'
            })

            // GET BY ID
            .addCase(GetManipulationDetailById.pending, (state) => {
                state.getManipulationDetailByIdStatus = 'loading'
            })
            .addCase(GetManipulationDetailById.fulfilled, (state, action) => {
                state.getManipulationDetailByIdStatus = 'successfully'
                state.getManipulationDetailByIdResponse = action.payload
            })
            .addCase(GetManipulationDetailById.rejected, (state, action) => {
                state.getManipulationDetailByIdStatus = 'failed'
            })

            // DELETE
            .addCase(ManipulationDetailDelete.pending, (state) => {
                state.manipulationDetailDeleteStatus = 'loading'
            })
            .addCase(ManipulationDetailDelete.fulfilled, (state) => {
                state.manipulationDetailDeleteStatus = 'successfully'
            })
            .addCase(ManipulationDetailDelete.rejected, (state, action) => {
                state.manipulationDetailDeleteStatus = 'failed'
            })
    },
})

export const {
    // REQUEST
    setMDCreationCompanyName,
    setMDCreationContact,
    setMDCreationCoordinates,
    setMDCreationGGGNGLN,
    setMDCreationGGNotificationId,
    setMDCreationHeadquarter,
    setMDCreationIsCertified,
    setMDCreationParallelProperty,
    setMDCreationProducts,
    setMDCreationThirdPartyPackaging,
    setMDCreationVatOrFiscalCode,
    setMDFiltersGGNotificationId,
    setMDFiltersItemsPerPage,
    setMDFiltersPage,
    resetManipulationDetailFilters,
    resetManipulationDetailRequest,

    // STATUS
    resetGetAllManipulationDetailStatus,
    resetGetManipulationDetailByIdStatus,
    resetManipulationDetailCreationStatus,
    resetManipulationDetailDeleteStatus
} = ManipulationDetailSlice.actions

export default ManipulationDetailSlice.reducer